import React from 'react';
import { Empty, Tag, Typography, Collapse, Card } from 'antd';
import { ConfigTableRowDiff, SubsystemDiff } from '../../../services/ai-admin-api';
import { SideBySideLineDiff } from './SideBySideLineDiff';

const { Text } = Typography;

interface Props {
    diff: SubsystemDiff | null;
}

const KIND_COLOUR: Record<string, string> = {
    added: 'green',
    removed: 'red',
    changed: 'orange',
};

const SKIP_FIELDS = new Set(['id', 'created_at', 'updated_at']);

function valueToString(v: unknown): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    return JSON.stringify(v, null, 2);
}

const FieldDiff: React.FC<{
    field: string;
    before: unknown;
    after: unknown;
}> = ({ field, before, after }) => (
    <div style={{ marginBottom: 12 }}>
        <Text code style={{ fontSize: 12 }}>
            {field}
        </Text>
        <div style={{ marginTop: 4 }}>
            <SideBySideLineDiff before={valueToString(before)} after={valueToString(after)} />
        </div>
    </div>
);

const RowDiffBody: React.FC<{ row: ConfigTableRowDiff }> = ({ row }) => {
    if (row.kind === 'changed') {
        const fields = row.changedFields ?? [];
        if (fields.length === 0) {
            return <Text type="secondary">No textual changes.</Text>;
        }
        return (
            <div>
                {fields.map((field) => (
                    <FieldDiff
                        key={field}
                        field={field}
                        before={(row.before as Record<string, unknown> | null | undefined)?.[field]}
                        after={(row.after as Record<string, unknown> | null | undefined)?.[field]}
                    />
                ))}
            </div>
        );
    }

    // Added or removed: walk every column on the present side, render against
    // empty on the other side. Skips id / timestamps so the diff focuses on
    // semantic content.
    const present = (row.kind === 'added' ? row.after : row.before) as
        | Record<string, unknown>
        | null
        | undefined;
    const otherSide: 'before' | 'after' = row.kind === 'added' ? 'before' : 'after';
    const fields = present
        ? Object.keys(present).filter((k) => !SKIP_FIELDS.has(k)).sort()
        : [];
    if (fields.length === 0) {
        return <Text type="secondary">(empty row)</Text>;
    }
    return (
        <div>
            {fields.map((field) => (
                <FieldDiff
                    key={field}
                    field={field}
                    before={otherSide === 'before' ? '' : present?.[field]}
                    after={otherSide === 'after' ? '' : present?.[field]}
                />
            ))}
        </div>
    );
};

/**
 * Renders the structured SubsystemDiff. system_setting changes shown as a
 * before/after pair; config-table row changes grouped per table with the
 * affected row keys + changed fields. Used by both the publish-confirm modal
 * and the version-history drill-in.
 */
export const DiffViewer: React.FC<Props> = ({ diff }) => {
    if (!diff || !diff.hasChanges) {
        return (
            <Empty
                description="No changes between these two versions."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
        );
    }

    return (
        <div>
            {diff.systemSettings.length > 0 && (
                <Card size="small" title="system_setting changes" style={{ marginBottom: 12 }}>
                    {diff.systemSettings.map((s) => (
                        <div key={s.name} style={{ marginBottom: 16 }}>
                            <Text code>{s.name}</Text>
                            <div style={{ marginTop: 4 }}>
                                <SideBySideLineDiff
                                    before={s.before ?? ''}
                                    after={s.after ?? ''}
                                />
                            </div>
                        </div>
                    ))}
                </Card>
            )}

            {Object.entries(diff.configTableRows).map(([table, rows]) => (
                <Card key={table} size="small" title={`${table} (${rows.length} row change${rows.length === 1 ? '' : 's'})`} style={{ marginBottom: 12 }}>
                    <Collapse
                        size="small"
                        items={rows.map((r) => ({
                            key: r.rowKey,
                            label: (
                                <span>
                                    <Tag color={KIND_COLOUR[r.kind]}>{r.kind}</Tag>
                                    <Text code>{r.rowKey}</Text>
                                    {r.changedFields && r.changedFields.length > 0 && (
                                        <Text type="secondary" style={{ marginLeft: 8 }}>
                                            ({r.changedFields.join(', ')})
                                        </Text>
                                    )}
                                </span>
                            ),
                            children: <RowDiffBody row={r} />,
                        }))}
                    />
                </Card>
            ))}
        </div>
    );
};
